import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, Plus, CheckSquare, Square, Edit, Trash2, X,
  Home, Truck, ShoppingBag, ClipboardList, Sparkles, Filter,
  ToggleLeft, ToggleRight, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { servicesAPI } from '../../services/api';

// Service type labels and colors - 4 Core Services (Rides PRIMARY, then Laundry, Courier, Errands)
// NO Cleaning, NO Private Concierge, NO Social Engagement
const serviceTypeConfig = {
  rides: { label: 'Rides', color: 'bg-[#8B5CF6]/10 text-[#8B5CF6]', icon: Truck },
  laundry: { label: 'Laundry', color: 'bg-blue-100 text-blue-700', icon: Sparkles },
  courier: { label: 'Courier/Delivery', color: 'bg-orange-100 text-orange-700', icon: Truck },
  errands: { label: 'Errands & Tasks', color: 'bg-[var(--success)]/15 text-green-700', icon: ShoppingBag }
};

const ServicesTab = ({
  services,
  servicesSelectMode,
  setServicesSelectMode,
  selectedServices,
  setSelectedServices,
  setShowServiceModal,
  handleEditService,
  handleDeleteService,
  fetchAllData
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [seeding, setSeeding] = useState(false);
  const [togglingServiceId, setTogglingServiceId] = useState(null);
  const [showInactive, setShowInactive] = useState(true);

  // Handle toggle service active status
  const handleToggleServiceStatus = async (service) => {
    setTogglingServiceId(service.id);
    try {
      const response = await servicesAPI.toggleStatus(service.id);
      const action = response.data.is_active ? 'activated' : 'deactivated';
      toast.success(`${service.name} ${action}`);
      fetchAllData();
    } catch (e) {
      console.error('Failed to toggle service status:', e);
      toast.error('Failed to update service status');
    } finally {
      setTogglingServiceId(null);
    }
  };

  // Get unique service types from services
  const serviceTypes = useMemo(() => {
    const types = [...new Set(services.map(s => s.service_type))].filter(Boolean);
    return types.sort();
  }, [services]);

  // Filter services based on active filter and show/hide inactive toggle
  const filteredServices = useMemo(() => {
    let filtered = services;
    
    // Filter by show inactive toggle
    if (!showInactive) {
      filtered = filtered.filter(s => s.is_active !== false);
    }
    
    // Filter by service type
    if (activeFilter !== 'all') {
      filtered = filtered.filter(s => s.service_type === activeFilter);
    }
    
    return filtered;
  }, [services, activeFilter, showInactive]);

  // Count services by type (including inactive for owner view)
  const typeCounts = useMemo(() => {
    const counts = { all: services.length };
    services.forEach(s => {
      const type = s.service_type || 'other';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [services]);
  
  // Count active/inactive
  const activeCount = useMemo(() => services.filter(s => s.is_active !== false).length, [services]);
  const inactiveCount = useMemo(() => services.filter(s => s.is_active === false).length, [services]);
  
  const handleBulkDeleteServices = async () => {
    if (!window.confirm(`Delete ${selectedServices.length} service(s)? This cannot be undone.`)) return;
    try {
      await servicesAPI.bulkDelete(selectedServices);
      toast.success(`${selectedServices.length} service(s) deleted`);
      setSelectedServices([]);
      setServicesSelectMode(false);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to delete some services');
    }
  };

  const handleSeedAllServices = async () => {
    if (!window.confirm('This will add all standard service categories (Rides, Laundry, Courier, Errands). Continue?')) return;
    setSeeding(true);
    try {
      const response = await servicesAPI.seedAll();
      toast.success(response.data.message);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to seed services');
    } finally {
      setSeeding(false);
    }
  };

  const getServiceTypeLabel = (type) => {
    return serviceTypeConfig[type]?.label || type?.toUpperCase() || 'SERVICE';
  };

  const getServiceTypeColor = (type) => {
    return serviceTypeConfig[type]?.color || 'bg-[var(--background-subtle)] text-[var(--text-primary)]';
  };

  const getServiceTypeIcon = (type) => {
    return serviceTypeConfig[type]?.icon || Briefcase;
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Services</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-[var(--text-muted)]">{services.length} total</p>
            <span className="text-[var(--success)] text-sm">• {activeCount} active</span>
            {inactiveCount > 0 && (
              <span className="text-[var(--text-muted)] text-sm">• {inactiveCount} inactive</span>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap justify-end">
          {/* Show/Hide Inactive Toggle */}
          {inactiveCount > 0 && (
            <Button 
              onClick={() => setShowInactive(!showInactive)} 
              variant="outline" 
              size="sm"
              className={!showInactive ? 'bg-[var(--background-subtle)]' : ''}
              data-testid="toggle-show-inactive"
            >
              {showInactive ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
              {showInactive ? 'Hide Inactive' : 'Show Inactive'}
            </Button>
          )}
          {servicesSelectMode && selectedServices.length > 0 && (
            <>
              <Button 
                onClick={handleBulkDeleteServices}
                variant="destructive" 
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete ({selectedServices.length})
              </Button>
              <Button onClick={() => { setSelectedServices([]); setServicesSelectMode(false); }} variant="ghost" size="sm">
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
          {!servicesSelectMode && services.length > 0 && (
            <Button onClick={() => setServicesSelectMode(true)} variant="outline" size="sm">
              <CheckSquare className="w-4 h-4 mr-2" />
              Select Multiple
            </Button>
          )}
          <Button 
            onClick={handleSeedAllServices} 
            variant="outline" 
            size="sm"
            disabled={seeding}
            data-testid="seed-services-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {seeding ? 'Seeding...' : 'Seed All Categories'}
          </Button>
          <Button onClick={() => setShowServiceModal(true)} className="btn-primary" data-testid="add-service-btn">
            <Plus className="w-5 h-5 mr-2" />
            Add Service
          </Button>
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="mb-6 p-2 bg-[var(--background-subtle)] rounded-xl overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          <button
            onClick={() => setActiveFilter('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeFilter === 'all' 
                ? 'bg-[var(--background-paper)] shadow-sm text-[var(--primary)] border border-[var(--primary)]/20' 
                : 'text-[var(--text-secondary)] hover:bg-[var(--background-paper)]/50'
            }`}
          >
            <Filter className="w-4 h-4" />
            All Services
            <span className={`px-2 py-0.5 text-xs rounded-full ${activeFilter === 'all' ? 'bg-[var(--primary)]/10' : 'bg-[var(--background-subtle)]'}`}>
              {typeCounts.all}
            </span>
          </button>
          
          {serviceTypes.map(type => {
            const Icon = getServiceTypeIcon(type);
            return (
              <button
                key={type}
                onClick={() => setActiveFilter(type)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeFilter === type 
                    ? 'bg-[var(--background-paper)] shadow-sm text-[var(--primary)] border border-[var(--primary)]/20' 
                    : 'text-[var(--text-secondary)] hover:bg-[var(--background-paper)]/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {getServiceTypeLabel(type)}
                <span className={`px-2 py-0.5 text-xs rounded-full ${activeFilter === type ? 'bg-[var(--primary)]/10' : 'bg-[var(--background-subtle)]'}`}>
                  {typeCounts[type] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Select All in select mode */}
      {servicesSelectMode && filteredServices.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedServices(filteredServices.map(s => s.id))}
          >
            Select All ({filteredServices.length})
          </Button>
          <span className="text-sm text-[var(--text-muted)]">
            {selectedServices.length} selected
          </span>
        </div>
      )}
      
      {filteredServices.length === 0 ? (
        <div className="card p-12 text-center">
          <Briefcase className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <p className="text-[var(--text-secondary)] mb-4">
            {activeFilter === 'all' ? 'No services yet. Add your first service!' : `No ${getServiceTypeLabel(activeFilter)} services.`}
          </p>
          <Button onClick={() => setShowServiceModal(true)} className="btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            Add Service
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map(service => {
            const Icon = getServiceTypeIcon(service.service_type);
            const isActive = service.is_active !== false;
            const isToggling = togglingServiceId === service.id;
            
            return (
              <div 
                key={service.id} 
                className={`card p-6 relative transition-all ${
                  servicesSelectMode && selectedServices.includes(service.id) ? 'ring-2 ring-[var(--primary)]' : ''
                } ${!isActive ? 'opacity-60 bg-[var(--background-subtle)]' : ''}`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Inactive Banner */}
                {!isActive && (
                  <div className="absolute top-0 left-0 right-0 bg-[var(--text-muted)] text-white text-xs text-center py-1 rounded-t-lg font-medium">
                    INACTIVE - Not visible to customers
                  </div>
                )}
                
                <div className={`flex justify-between items-start mb-4 ${!isActive ? 'mt-4' : ''}`}>
                  <div className="flex items-center gap-2">
                    {servicesSelectMode && (
                      <button 
                        onClick={() => setSelectedServices(prev => 
                          prev.includes(service.id) ? prev.filter(id => id !== service.id) : [...prev, service.id]
                        )}
                        className="flex-shrink-0"
                      >
                        {selectedServices.includes(service.id) ? (
                          <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getServiceTypeColor(service.service_type)}`}>
                      <Icon className="w-3 h-3" />
                      {getServiceTypeLabel(service.service_type)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm px-2 py-0.5 rounded-full ${
                      service.package_type === 'add_on' ? 'bg-amber-100 text-amber-700' :
                      service.package_type === 'package' ? 'bg-[#F4C2C2]/15 text-purple-700' :
                      service.package_type === 'recurring' ? 'bg-[var(--success)]/15 text-green-700' :
                      'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                    }`}>
                      {service.package_type === 'add_on' ? '+ ADD-ON' : 
                       service.package_type === 'package' ? 'PACKAGE' :
                       service.package_type?.toUpperCase() || 'HOURLY'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditService(service)}
                      className="text-[#D4AF37] hover:text-[#D4AF37]"
                      data-testid={`edit-service-${service.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteService(service.id)}
                      className="text-[var(--error)] hover:text-red-700"
                      data-testid={`delete-service-${service.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <h3 className={`font-bold text-lg mb-2 ${!isActive ? 'text-[var(--text-muted)]' : ''}`}>{service.name}</h3>
                <p className="text-sm text-[var(--text-secondary)] mb-4 line-clamp-2">{service.description}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-xl font-bold ${isActive ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'}`}>${service.hourly_rate}/hr</span>
                    <span className="text-sm text-[var(--text-muted)] ml-2">• {service.duration_hours}h</span>
                  </div>
                  {service.recurring_discount > 0 && (
                    <span className="text-sm text-[var(--success)]">{service.recurring_discount}% off</span>
                  )}
                </div>
                
                {/* Active/Inactive Toggle */}
                <div className="mt-4 pt-4 border-t border-[var(--border-color)] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isActive ? (
                      <ToggleRight className="w-4 h-4 text-[var(--success)]" />
                    ) : (
                      <ToggleLeft className="w-4 h-4 text-[var(--text-muted)]" />
                    )}
                    <span className={`text-sm font-medium ${isActive ? 'text-[var(--success)]' : 'text-[var(--text-muted)]'}`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggleServiceStatus(service)}
                    disabled={isToggling}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
                      isActive ? 'bg-[var(--success)]' : 'bg-gray-300'
                    } ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                    data-testid={`toggle-service-${service.id}`}
                    aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${service.name}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default ServicesTab;
